import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { 
  Calendar, 
  Clock, 
  User,
  LogOut,
  Plus,
  Trash2,
  Mail,
  Briefcase
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { API, useAuth } from "../App";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API}/bookings`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    }
    setLoading(false);
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/bookings/${bookingId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Booking cancelled");
        fetchBookings();
      } else {
        throw new Error("Failed to cancel");
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toast.error("Failed to cancel booking");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getServiceLabel = (value) => {
    const services = {
      "custom-ai": "Custom AI Solutions",
      "lead-gen": "Lead Generation Systems",
      "automation": "Workflow Automation",
      "consultation": "General Consultation"
    };
    return services[value] || value;
  };

  const upcomingBookings = bookings.filter(b => 
    b.status !== "cancelled" && new Date(b.date) >= new Date()
  );
  
  const pastBookings = bookings.filter(b => 
    b.status !== "cancelled" && new Date(b.date) < new Date()
  );
  
  const cancelledBookings = bookings.filter(b => b.status === "cancelled");

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center space-x-2">
            <span className="font-bold text-xl text-[#FF4F00]" style={{ fontFamily: 'Syne, sans-serif' }}>
              Yantra
            </span>
            <span className="font-bold text-xl text-[#1A1A1A]" style={{ fontFamily: 'Syne, sans-serif' }}>
              AI
            </span>
          </a>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-[#FF4F00] rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {user?.name?.charAt(0) || "U"}
                </div>
              )}
              <span className="text-sm font-medium text-[#1A1A1A] hidden sm:inline">
                {user?.name}
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-[#64748B]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 
              className="text-2xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="dashboard-title"
            >
              My Bookings
            </h1>
            <p className="text-[#64748B] mt-1">Manage your scheduled calls</p>
          </div>
          <Button 
            className="btn-primary"
            onClick={() => navigate("/booking")}
            data-testid="new-booking-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Book New Call
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF4F00]"></div>
          </div>
        ) : bookings.length === 0 ? (
          <motion.div 
            className="bg-white rounded-2xl p-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Calendar className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">No bookings yet</h3>
            <p className="text-[#64748B] mb-6">Schedule your first discovery call to get started.</p>
            <Button 
              className="btn-primary"
              onClick={() => navigate("/booking")}
            >
              Book a Call
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-8">
            {/* Upcoming Bookings */}
            {upcomingBookings.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4">Upcoming</h2>
                <div className="grid gap-4">
                  {upcomingBookings.map((booking, index) => (
                    <motion.div
                      key={booking.booking_id}
                      className="bg-white rounded-2xl p-6 border border-gray-100"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      data-testid={`booking-${booking.booking_id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#FF4F00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Calendar className="w-6 h-6 text-[#FF4F00]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1A1A1A]">
                              Discovery Call
                            </h3>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-[#64748B]">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {format(parseISO(booking.date), "EEE, MMM d, yyyy")}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {booking.time_slot}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 mt-2 text-sm text-[#64748B]">
                              <Briefcase className="w-4 h-4" />
                              {getServiceLabel(booking.service_type)}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelBooking(booking.booking_id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`cancel-booking-${booking.booking_id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Past Bookings */}
            {pastBookings.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4">Past</h2>
                <div className="grid gap-4">
                  {pastBookings.map((booking, index) => (
                    <motion.div
                      key={booking.booking_id}
                      className="bg-white rounded-2xl p-6 border border-gray-100 opacity-60"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 0.6, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-6 h-6 text-[#64748B]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1A1A1A]">
                            Discovery Call
                          </h3>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-[#64748B]">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {format(parseISO(booking.date), "EEE, MMM d, yyyy")}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {booking.time_slot}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Cancelled Bookings */}
            {cancelledBookings.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4">Cancelled</h2>
                <div className="grid gap-4">
                  {cancelledBookings.map((booking, index) => (
                    <motion.div
                      key={booking.booking_id}
                      className="bg-white rounded-2xl p-6 border border-gray-100 opacity-40"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 0.4, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1A1A1A] line-through">
                            Discovery Call
                          </h3>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-[#64748B]">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {format(parseISO(booking.date), "EEE, MMM d, yyyy")}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {booking.time_slot}
                            </span>
                          </div>
                          <span className="inline-block mt-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                            Cancelled
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
